import axios from "axios";
import { handleRequestError, memoizePromiseFn, getCustomerRtu, getRTUDetails } from "../../requests";

// -----------------------------------------------------------------
// List queries (server-side paging/sorting/filtering via QueryDefinition)
// -----------------------------------------------------------------

//getPlacedOrders
export const getPlacedOrders = async (params) => {
    const response = await axios.post('/Orders/getPlacedOrders', params).catch(handleRequestError);
    return response.data;
}

//getAllOrders: server-side paged (QueryDefinition).
export const getAllOrders = async (params) => {
    const response = await axios.post('/Orders/GetAllOrders', params).catch(handleRequestError);
    return response.data;
}

//savedOrders
export const getSavedOrders = async () => {
    const response = await axios.post('/Orders/GetSavedOrders').catch(handleRequestError);
    return response.data;
}

// Alias kept for legacy callers; routes to saved orders.
export const getOrders = getSavedOrders;

// "Show Records for last N days" window; blank/invalid falls back to the legacy default of 1.
const daysParam = (days) => {
    const n = parseInt(days, 10);
    return Number.isNaN(n) ? 1 : n;
}

export const getCanceledOrders = async (params) => {
    const response = await axios.post('/Orders/GetCanceledOrders', null, { params: { days: daysParam(params?.days) } }).catch(handleRequestError);
    return response.data;
}

export const getRecentlyShippedOrders = async (params) => {
    const response = await axios.post('/Orders/GetRecentlyShippedOrders', null, { params: { days: daysParam(params?.days) } }).catch(handleRequestError);
    return response.data;
}

export const getCustomerChangedOrders = async () => {
    const response = await axios.post('/Orders/GetCustomerChangedOrders').catch(handleRequestError);
    return response.data;
}

export const getMyRecentOrders = async () => {
    const response = await axios.post('/Orders/GetMyRecentOrders').catch(handleRequestError);
    return response.data;
}

// All Orders list-page tab badge counts in one request, computed under the shared
// filters (Order Type, Source Plant, last-N-days). Lets the page lazy-load each
// tab's grid while keeping every badge live and correct.
export const getOrderTabCounts = async (payload) => {
    const response = await axios.post('/Orders/GetOrderTabCounts', payload).catch(handleRequestError);
    return response.data;
}

// -----------------------------------------------------------------
// Single-order reads
// -----------------------------------------------------------------

export const getOrderById = async (id) => {
    const response = await axios.get(`/Orders/GetOrderById/${id}`).catch(handleRequestError);
    return response.data;
}

export const getOrderIdByNumber = async (orderNumber: number): Promise<number> => {
    const response = await axios.get<number>(`/Orders/GetOrderIdByNumber/${orderNumber}`);
    return response.data;
}

export const getOrderDetails = async (id) => {
    const response = await axios.get(`/Orders/GetOrderDetails/${id}`).catch(handleRequestError);
    return response.data;
}

export const getOrderAudit = async (id) => {
    const response = await axios.get(`/Orders/GetOrderAudit/${id}`).catch(handleRequestError);
    return response.data;
}

export const getOrderAttachments = async (id) => {
    const response = await axios.get(`/Orders/GetOrderAttachments/${id}`).catch(handleRequestError);
    return response.data;
}

export const getOrderDewarShipments = async (id) => {
    const response = await axios.get(`/Orders/GetOrderDewarShipments/${id}`).catch(handleRequestError);
    return response.data;
}

// Products Delivered serial-number autocomplete (legacy GetAllDewarContainers).
export const searchDewarContainers = async (prefix = '') => {
    const qs = prefix ? `?prefix=${encodeURIComponent(prefix)}` : '';
    const response = await axios.get(`/Orders/SearchDewarContainers${qs}`).catch(handleRequestError);
    return response.data;
}

// -----------------------------------------------------------------
// Lookups (memoized — small static reference tables)
// -----------------------------------------------------------------

const getOrderTypesReq = async () => {
    const response = await axios.get('/Orders/GetOrderTypes').catch(handleRequestError);
    return response.data;
}
export const getOrderTypes = memoizePromiseFn(getOrderTypesReq);

// Delivery types per order type are a small static reference table. Memoized
// (keyed by orderTypeId) so the order-edit/create dropdowns don't re-fetch on
// every page load or reloadOnFields re-trigger — same treatment as getOrderTypes.
const getOrderDeliveryTypesReq = async (orderTypeId) => {
    const response = await axios.get(`/Orders/GetDeliveryTypes/${orderTypeId}`).catch(handleRequestError);
    return response.data;
}
export const getOrderDeliveryTypes = memoizePromiseFn(getOrderDeliveryTypesReq);

// Carrier dropdown source — mirrors the legacy PopulateCarriers(): Dewar orders
// get dewar-only carriers, all other order types get standard carriers. Memoized
// (keyed by orderTypeId) — a small static reference table, like getOrderTypes.
const getCarriersReq = async (orderTypeId) => {
    const response = await axios.get(`/Orders/GetCarriers/${orderTypeId}`).catch(handleRequestError);
    return response.data;
}
export const getCarriers = memoizePromiseFn(getCarriersReq);

// Driver dropdown source — mirrors the legacy PopulateDrivers(). Returns the
// full application user list; the edit form filters to active users and maps
// the legacy username (LegacyId) to Order.DriverUserId.
const getActiveUsersReq = async () => {
    const response = await axios.get('/Administration/Users').catch(handleRequestError);
    return response.data;
}
export const getActiveUsers = memoizePromiseFn(getActiveUsersReq);

// Dewar credit-card status — leverages the existing Billing domain endpoint
// (mirrors the legacy GetCreditCardStatus()). Returns a CreditCardLookupDTO.
export const creditCardLookup = async (cst) => {
    const response = await axios.post('/DewarBilling/CreditCardLookup', { cst: String(cst ?? '') }).catch(handleRequestError);
    return response.data;
}

// Customer defaults — mirrors the legacy SetCustomerInfo(). Returns the field
// values to auto-populate when the Customer dropdown changes on the Order
// Information tab (address block, PO, recommended shipping location, default
// delivery type, notes, and the Dewar context labels).
export const getCustomerDefaults = async (orderTypeId, customerId) => {
    const response = await axios
        .get(`/Orders/GetCustomerDefaults/${orderTypeId}/${encodeURIComponent(String(customerId ?? ''))}`)
        .catch(handleRequestError);
    return response.data;
}

export interface OrderContact {
    name: string;
    phone: string;
    email: string;
    fax: string;
}

export const getCustomerContacts = async (orderTypeId: number, customerId: number): Promise<OrderContact[]> => {
    const response = await axios.get<OrderContact[]>(`/Orders/GetCustomerContacts/${orderTypeId}/${customerId}`);
    return response.data;
}

// Legacy btnCreditCardOverride_Click — raises the order's CreditCardOverride
// flag; the external Credit Card Processing Application then pre-authorizes the
// card. Only the flag is set here (the charge itself happens out of band).
export const setCreditCardOverride = async (id) => {
    const response = await axios.put(`/Orders/SetCreditCardOverride/${id}`).catch(handleRequestError);
    return response.data;
}

const getCasOrderStatesReq = async () => {
    const response = await axios.get('/Orders/GetCasOrderStates').catch(handleRequestError);
    return response.data;
}
export const getCasOrderStates = memoizePromiseFn(getCasOrderStatesReq);

// Source Plant (HDC) filter options — mirrors the legacy GetAllLocations dropdown.
// Returns sorted, distinct location names for use as combo filter options.
// Uses the unfiltered all-locations endpoint so Orders users see every location
// regardless of their location permissions (legacy parity).
const getSourcePlantOptionsReq = async () => {
    const response = await axios.get('/Location/GetAllLocations').catch(handleRequestError);
    const locations = response.data ?? [];
    return Array.from(new Set(locations.map((l) => l.locationName).filter(Boolean)))
        .sort((a, b) => a.trim().localeCompare(b.trim()));
}
export const getSourcePlantOptions = memoizePromiseFn(getSourcePlantOptionsReq);

// -----------------------------------------------------------------
// Writes
// -----------------------------------------------------------------

export const insertOrder = async (order) => {
    const response = await axios.post('/Orders/InsertOrder', order).catch(handleRequestError);
    return response.data;
}

export const updateOrder = async (order) => {
    // Edit uses full timestamps; legacy time companions from the loaded order
    // would otherwise override those timestamps on the server.
    const { dateRequestedHour, driverArrivalHour, dateFulfilledTime, ...payload } = order;
    const response = await axios.put('/Orders/UpdateOrder', payload).catch(handleRequestError);
    return response.data;
}

export const cancelOrder = async (payload) => {
    const response = await axios.put('/Orders/CancelOrder', payload).catch(handleRequestError);
    return response.data;
}

// Legacy btnUnCancelOrder_Click — reverses a cancellation.
export const unCancelOrder = async (payload) => {
    const response = await axios.put('/Orders/UnCancelOrder', payload).catch(handleRequestError);
    return response.data;
}

// Legacy btnDelete_Click — soft-deletes the order (isDeleted flag).
export const deleteOrder = async (payload) => {
    const response = await axios.put('/Orders/DeleteOrder', payload).catch(handleRequestError);
    return response.data;
}

// Legacy btnCreateOrder_Click ("Place Order") — assigns the next OrderNumber.
export const placeOrder = async (id) => {
    const response = await axios.put(`/Orders/PlaceOrder/${id}`).catch(handleRequestError);
    return response.data;
}

// Legacy btnBulkOrder_Click — places a bulk-created order (assigns OrderNumber,
// audits "Bulk Order Placed"). Call after insertOrder for each Multi-Order row.
export const placeBulkOrder = async (id) => {
    const response = await axios.put(`/Orders/PlaceBulkOrder/${id}`).catch(handleRequestError);
    return response.data;
}

// Legacy btnAcceptCustChanges_Click — accepts CAS customer changes: sets CAS
// state to Placed, audits, and emails the change acknowledgment. Call AFTER the
// form is saved (mirrors legacy SavePageHandler(false, true)).
export const acceptCustomerChanges = async (id) => {
    const response = await axios.put(`/Orders/AcceptCustomerChanges/${id}`).catch(handleRequestError);
    return response.data;
}

// Legacy GeneratePickupOrder — creates a Dewar pickup child order from a parent.
export const generatePickupOrder = async (payload) => {
    const response = await axios.post('/Orders/GeneratePickupOrder', payload).catch(handleRequestError);
    return response.data;
}

// -----------------------------------------------------------------
// Product Manifest (dbo.OrderManifest) CRUD — legacy Dewar manifest grid.
// -----------------------------------------------------------------

export const getOrderManifests = async (orderId) => {
    const response = await axios.get(`/Orders/GetOrderManifests/${orderId}`).catch(handleRequestError);
    return response.data;
}

export const addOrderManifest = async (payload) => {
    const response = await axios.post('/Orders/AddOrderManifest', payload).catch(handleRequestError);
    return response.data;
}

export const updateOrderManifest = async (payload) => {
    const response = await axios.put('/Orders/UpdateOrderManifest', payload).catch(handleRequestError);
    return response.data;
}

export const deleteOrderManifest = async (id) => {
    const response = await axios.delete(`/Orders/DeleteOrderManifest/${id}`).catch(handleRequestError);
    return response.data;
}

// -----------------------------------------------------------------
// Products Delivered (dbo.OrderDetails / dewar shipments) CRUD.
// -----------------------------------------------------------------

export const addOrderDewarShipment = async (payload) => {
    const response = await axios.post('/Orders/AddOrderDewarShipment', payload).catch(handleRequestError);
    return response.data;
}

export const updateOrderDewarShipment = async (payload) => {
    const response = await axios.put('/Orders/UpdateOrderDewarShipment', payload).catch(handleRequestError);
    return response.data;
}

export const deleteOrderDewarShipment = async (id) => {
    const response = await axios.delete(`/Orders/DeleteOrderDewarShipment/${id}`).catch(handleRequestError);
    return response.data;
}

// Legacy "Auto Reprice Order Items" button — recomputes product unit prices +
// re-runs the shipped fee routines, returning the refreshed delivered-products.
export const repriceOrderShipments = async (orderId) => {
    const response = await axios.post(`/Orders/RepriceOrderShipments/${orderId}`).catch(handleRequestError);
    return response.data;
}

// Legacy Shipping Location Status grid — upcoming orders for the shipping location.
export const getUpcomingOrdersForLocation = async (orderTypeId, shippingLocationId) => {
    if (!orderTypeId || !shippingLocationId) return [];
    const response = await axios.get(`/Orders/GetUpcomingOrdersForLocation/${orderTypeId}/${encodeURIComponent(shippingLocationId)}`).catch(handleRequestError);
    return response.data;
}

// Current Customer tab — upcoming orders for this customer (legacy gvFutureDeliveriesByCustomer).
export const getUpcomingOrdersForCustomer = async (orderTypeId, customerId) => {
    if (!orderTypeId || !customerId) return [];
    const response = await axios.get(`/Orders/GetUpcomingOrdersForCustomer/${orderTypeId}/${customerId}`).catch(handleRequestError);
    return response.data;
}

// Current Customer tab — recently fulfilled Dewar orders (legacy gvRecentDewarDeliveries).
export const getRecentDeliveredDewarOrders = async (customerId) => {
    if (!customerId) return [];
    const response = await axios.get(`/Orders/GetRecentDeliveredDewarOrders/${customerId}`).catch(handleRequestError);
    return response.data;
}

// Current Customer tab — the customer's recent Gas deliveries (legacy gvRecentDeliveries /
// ListGasBillingRecentDeliveries): Date Shipped / Order # / Volume Delivered.
export const getOrderGasRecentDeliveries = async (customerId) => {
    if (!customerId) return [];
    const response = await axios.get(`/Orders/GetGasRecentDeliveries/${customerId}`).catch(handleRequestError);
    return response.data;
}

// Current Customer tab — the customer's recent Liquid deliveries (legacy gvRecentDeliveries /
// ListLiquidBillingRecentDeliveries): Date Shipped / Order # / Volume Delivered.
export const getOrderLiquidRecentDeliveries = async (customerId) => {
    if (!customerId) return [];
    const response = await axios.get(`/Orders/GetLiquidRecentDeliveries/${customerId}`).catch(handleRequestError);
    return response.data;
}

// Current Customer tab — outstanding dewar containers on the customer's site (legacy gvDewarContainersOnSite).
export const getDewarContainersOnSite = async (customerId) => {
    if (!customerId) return [];
    const response = await axios.get(`/Orders/GetDewarContainersOnSite/${customerId}`).catch(handleRequestError);
    return response.data;
}

// Current Customer tab — gas containers currently on the customer's site (legacy gvContainersOnSite).
export const getGasContainersOnSite = async (customerId) => {
    if (!customerId) return [];
    const response = await axios.get(`/Orders/GetGasContainersOnSite/${customerId}`).catch(handleRequestError);
    return response.data;
}

// Current Customer tab — liquid containers currently on the customer's site (legacy gvContainersOnSite).
export const getLiquidContainersOnSite = async (customerId) => {
    if (!customerId) return [];
    const response = await axios.get(`/Orders/GetLiquidContainersOnSite/${customerId}`).catch(handleRequestError);
    return response.data;
}

// Current Customer tab — customer purity requirements (legacy divCustomerPurity).
export const getCustomerPurityRequirements = async (orderTypeId, customerId) => {
    if (!orderTypeId || !customerId) return [];
    const response = await axios.get(`/Orders/GetCustomerPurityRequirements/${orderTypeId}/${customerId}`).catch(handleRequestError);
    return response.data;
}

// Booking Information tab — assignable bookings for the booking dropdown
// (legacy PopulateBookings / GetAllBookingNumbersWithNoOrder).
export const getAvailableBookings = async (customerId, currentBookingId = null, includeAll = false) => {
    const params = new URLSearchParams();
    if (currentBookingId != null) params.append('currentBookingId', String(currentBookingId));
    if (includeAll) params.append('includeAll', 'true');
    const qs = params.toString();
    const response = await axios.get(`/Orders/GetAvailableBookings/${customerId}${qs ? `?${qs}` : ''}`).catch(handleRequestError);
    return response.data;
}

// Container Information tab — container-select dropdown options (legacy PopulateContainers).
export const getContainerOptions = async (orderTypeId, shippingLocationId = null, includeAll = false, currentContainerId = null) => {
    const params = new URLSearchParams();
    if (shippingLocationId != null) params.append('shippingLocationId', String(shippingLocationId));
    if (includeAll) params.append('includeAll', 'true');
    if (currentContainerId != null) params.append('currentContainerId', String(currentContainerId));
    const qs = params.toString();
    const response = await axios.get(`/Orders/GetContainerOptions/${orderTypeId}${qs ? `?${qs}` : ''}`).catch(handleRequestError);
    return response.data;
}

// Container Information tab — returning-container dropdown options.
export const getReturningContainerOptions = async (orderTypeId, customerId, includeAll = false) => {
    const qs = includeAll ? '?includeAll=true' : '';
    const response = await axios.get(`/Orders/GetReturningContainerOptions/${orderTypeId}/${customerId}${qs}`).catch(handleRequestError);
    return response.data;
}

// Container Information tab — customer requirements + selected container specs (legacy SetContainerInfo).
export const getContainerInfo = async (orderTypeId, customerId, containerId = null, currentOrderId = 0) => {
    if (!orderTypeId || !customerId) return null;
    const params = new URLSearchParams();
    if (containerId) params.append('containerId', String(containerId));
    if (currentOrderId) params.append('currentOrderId', String(currentOrderId));
    const qs = params.toString();
    const response = await axios.get(`/Orders/GetContainerInfo/${orderTypeId}/${customerId}${qs ? `?${qs}` : ''}`).catch(handleRequestError);
    return response.data;
}

// Billing Information tab — per-order-type billing summary sections (legacy SetBillingInfo).
export const getOrderBillingInfo = async (orderId) => {
    const response = await axios.get(`/Orders/GetOrderBillingInfo/${orderId}`).catch(handleRequestError);
    return response.data;
}

// Operations Information tab — Liquid/Gas fill/ship/inventory process sections (legacy SetTrackingInfo).
export const getOrderOperationsInfo = async (orderId) => {
    const response = await axios.get(`/Orders/GetOrderOperationsInfo/${orderId}`).catch(handleRequestError);
    return response.data;
}

// Downloads a single liquid tracking attachment (legacy dgTrackingAttachments
// download). Streams the file via a blob so the MSAL bearer token is attached,
// then triggers a browser download using the stored file name.
export const downloadTrackingAttachment = async (id, fileName) => {
    const results = await axios.get(`/Orders/GetTrackingAttachment/${id}`, { responseType: 'blob' }).catch(handleRequestError);
    if (!results) return;
    const url = window.URL.createObjectURL(new Blob([results.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName || `attachment-${id}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

// --- Order File Attachments (legacy dbo.GlobalAttachments, ModuleName "Orders") ---
// The Order edit page attachments panel reads/writes the SAME legacy
// GlobalAttachments rows as the WebForms page (not the newer dbo.Attachment store).
export const uploadOrderAttachment = async (orderId, description, file) => {
    const form = new FormData();
    form.append('orderId', String(orderId));
    form.append('description', description ?? '');
    form.append('file', file, file.name);
    const response = await axios.post('/Orders/UploadOrderAttachment', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
}

export const deleteOrderAttachment = async (id) => {
    const response = await axios.delete(`/Orders/DeleteOrderAttachment/${id}`).catch(handleRequestError);
    return response?.data;
}

export const updateOrderAttachmentDescription = async (id, description) => {
    const form = new FormData();
    form.append('description', description ?? '');
    const response = await axios.put(`/Orders/UpdateOrderAttachmentDescription/${id}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
}

export const downloadOrderAttachment = async (id, fileName) => {
    const results = await axios.get(`/Orders/GetOrderAttachment/${id}`, { responseType: 'blob' }).catch(handleRequestError);
    if (!results) return;
    const url = window.URL.createObjectURL(new Blob([results.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName || `attachment-${id}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

// Current Customer tab — Gas "Containers On Site" Receiver Bank checkbox toggle
// (legacy Orders.UpdateIsReceiverBank on GasTrackingNumbers — Gas-only feature).
export const updateIsReceiverBank = async (trackingNumberId, isReceiverBank) => {
    const response = await axios.put('/Orders/UpdateIsReceiverBank', { trackingNumberId, isReceiverBank });
    return response?.data;
}

// RTU Information tab — the gas customer's RTU readings for the chart (legacy
// SetCurrentCustomerRTUInfo). Resolves the customer's RTU/channel, then loads the
// reading series; returns null when the customer has no Helium RTU. The usage
// delta (psi/day over the last 7 days) comes from the readings' AvgUsage.
export const getCustomerRtuReadings = async (customerId, days = 7) => {
    if (!customerId) return null;
    const rtu = await getCustomerRtu(customerId);
    if (!rtu?.hasRtu) return null;
    const readings = await getRTUDetails(rtu.rtuId, rtu.channelId, days, true);
    const list = readings ?? [];
    const avg = list.length ? list[0]?.avgUsage : null;
    return {
        hasRtu: true,
        readings: list,
        delta: avg != null ? `Usage Delta (over last 7 days): ${avg} psi/day` : null,
    };
}

// Legacy btnMiscBilling_Click — creates a Misc. billing header from the order.
// The Orders/GenerateMiscBilling endpoint orchestrates the Misc billing
// repository insert AND the "Misc Billing Generated" order audit (mirroring the
// single legacy handler), returning the created header (incl. billingId).
export const createMiscBillingFromOrder = async (orderId) => {
    const response = await axios.post('/Orders/GenerateMiscBilling', { orderId }).catch(handleRequestError);
    return response.data;
}
